/**
 * frontend/lib/referral.js — the invite (referral) code a visitor arrived with.
 *
 * Codes come from the backend's generateReferralCode(): 8 characters of base64url, upper-cased —
 * A–Z, 0–9, '-' and '_'. A trailing '-' is legitimate ("NRKR2AF-"), so normalising keeps it.
 *
 * Stored as JSON { code, savedAt } under sounddao_referral_code and ignored (and removed) after
 * 30 days. The legacy plain-string value is still read. lib/userStorage clears the key on sign-out
 * and when a different user signs in; PersonaOnboarding removes it after /onboard.
 *
 * verifyReferrer(code) asks GET /api/users/referrer/:code → { valid, display_name }.
 */

export const REFERRAL_KEY = 'sounddao_referral_code';
export const REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const CODE_RE = /^[A-Z0-9_-]{4,16}$/;
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const store = () => { try { return window.localStorage; } catch { return null; } };

/** Trim, upper-case, drop anything outside the code alphabet → code or null. */
export function normalizeReferralCode(raw) {
  const c = String(raw == null ? '' : raw).trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '');
  return CODE_RE.test(c) ? c : null;
}

export function clearReferralCode() {
  try { store()?.removeItem(REFERRAL_KEY); } catch { /* storage unavailable */ }
}

/** Save a normalised code; a malformed one clears any stored code instead. → code or null. */
export function saveReferralCode(raw, now = Date.now()) {
  const code = normalizeReferralCode(raw);
  if (!code) { clearReferralCode(); return null; }
  try { store()?.setItem(REFERRAL_KEY, JSON.stringify({ code, savedAt: now })); } catch { /* storage unavailable */ }
  return code;
}

/** The stored code, or null (absent, malformed or older than 30 days — those are removed). */
export function readReferralCode(now = Date.now()) {
  let raw = null;
  try { raw = store()?.getItem(REFERRAL_KEY) ?? null; } catch { return null; }
  if (!raw) return null;
  let code = null;
  if (raw.trim().startsWith('{')) {
    try {
      const v = JSON.parse(raw);
      const fresh = typeof v?.savedAt === 'number' && now - v.savedAt <= REFERRAL_TTL_MS && now >= v.savedAt - 60000;
      code = fresh ? normalizeReferralCode(v.code) : null;
    } catch { code = null; }
  } else {
    code = normalizeReferralCode(raw);   // legacy plain string (no age known)
  }
  if (!code) clearReferralCode();
  return code;
}

/**
 * Who owns this code. → { valid: true, displayName } | { valid: false } | null (network/server
 * error: unknown — keep the code, show no inviter).
 */
export async function verifyReferrer(code, fetchImpl = fetch) {
  const c = normalizeReferralCode(code);
  if (!c) return { valid: false };
  try {
    const res = await fetchImpl(`${API_BASE}/api/users/referrer/${encodeURIComponent(c)}`);
    if (!res.ok) return null;
    const body = await res.json();
    if (body?.valid === true) return { valid: true, displayName: body.display_name || null };
    if (body?.valid === false) return { valid: false };
    return null;
  } catch {
    return null;
  }
}
